import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { Employees } from 'modules/employees/store/collections/employees'
import { usersWithRegularAccess } from 'modules/surveys/lib/usersWithRegularAccess'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  survey: Survey
  employees: Employees
}

export default function AddEditorModal({ survey, employees }: Props) {
  const { navigation } = useNavigation()

  const [userId, setUserId] = React.useState<number>()
  const [running, setRunning] = React.useState<boolean>(false)
  const navigate = useNavigate()
  const surveyId = Number(survey.attributes.id)

  const onClose = () => {
    const route = navigation.engagement.surveys.id.configuration.fullPath({ id: surveyId })
    navigate(route)
  }

  const submit = async (closePortal: ClosePortalFn) => {
    setRunning(true)

    const { success } = await asyncAction(() =>
      survey.rpc('supervisors', {
        access_id: userId,
        form_id: surveyId,
      })
    )
    setRunning(false)

    if (success) {
      survey.fetch({
        data: {
          surveys_form_id: surveyId,
        },
      })
      closePortal()
    }
  }

  const availableUsers = usersWithRegularAccess(users, employees)
  const memberIds = survey.attributes.supervisor_ids.map((access_id) => access_id) || []
  const notMembers = availableUsers.filter((user) => !memberIds.includes(user.attributes.id))
  const onSelect = (userId: number | null | undefined) => {
    userId && setUserId(userId)
  }

  return (
    <Modal
      title={i18n.t('surveys.form.add_manager.title')}
      description={i18n.t('surveys.form.add_manager.description')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={!userId || running} type="primary" aria-label="Add Editor button" submit>
          {i18n.t('surveys.form.add_manager.submit')}
        </Button>
      }
    >
      <UserSelector
        users={notMembers}
        onSelect={onSelect}
        selectedIds={[userId].filter(Boolean) as number[]}
        hideFields={[Fields.EmployeeStatus, Fields.UserType]}
      />
    </Modal>
  )
}
